import React, { useState, useEffect, useCallback } from 'react'
import { creditAppSettingsApi } from '../../services/creditAppSettings'
import type { CreditAppSetting } from '../../services/creditAppSettings'
import { Toast } from '../shared/Toast'

export function CreditAppSettings(): React.ReactElement | null {
  const [setting, setSetting] = useState<CreditAppSetting | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null)
  const [hasChanges, setHasChanges] = useState(false)

  // Local form state
  const [formEnabled, setFormEnabled] = useState(false)
  const [formCreditAppType, setFormCreditAppType] = useState('standard')
  const [formNotificationEmail, setFormNotificationEmail] = useState('')

  const showToast = useCallback((message: string, type: 'success' | 'error') => {
    setToast({ message, type })
  }, [])

  const syncFormState = useCallback((s: CreditAppSetting) => {
    setFormEnabled(s.enabled)
    setFormCreditAppType(s.credit_app_type || 'standard')
    setFormNotificationEmail(s.notification_email || '')
    setHasChanges(false)
  }, [])

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await creditAppSettingsApi.show()
      setSetting(data)
      syncFormState(data)
    } catch {
      setError('Failed to load credit app settings. Please try again.')
    } finally {
      setLoading(false)
    }
  }, [syncFormState])

  useEffect(() => {
    loadSettings()
  }, [loadSettings])

  const markChanged = () => setHasChanges(true)

  const handleSave = async () => {
    try {
      setSaving(true)
      const updated = await creditAppSettingsApi.update({
        enabled: formEnabled,
        credit_app_type: formCreditAppType,
        notification_email: formNotificationEmail || null,
      })
      setSetting(updated)
      syncFormState(updated)
      showToast('Credit app settings saved', 'success')
    } catch {
      showToast('Failed to save credit app settings', 'error')
    } finally {
      setSaving(false)
    }
  }

  // Loading state
  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-12">
        <div className="flex items-center justify-center gap-3 text-grey">
          <div className="w-5 h-5 border-2 border-gray-300 border-t-primary rounded-full animate-spin" />
          <span className="text-sm">Loading credit app settings...</span>
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
        <div className="w-12 h-12 bg-red-50 rounded-xl flex items-center justify-center mx-auto mb-4">
          <svg className="w-6 h-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
          </svg>
        </div>
        <p className="text-sm text-grey mb-4">{error}</p>
        <button onClick={loadSettings} className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90">
          Try Again
        </button>
      </div>
    )
  }

  if (!setting) return null

  return (
    <div className="space-y-6">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-secondary">Credit Applications</h1>
          <p className="text-sm text-grey mt-1">Configure credit application settings for your dealership.</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving || !hasChanges}
          className="inline-flex items-center gap-2 px-5 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {saving && <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {/* Settings Form */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="text-sm font-semibold text-secondary">Credit App Configuration</h2>
        </div>
        <div className="p-6 space-y-5">
          {/* Enable/Disable Toggle */}
          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-secondary">Enable Credit Applications</label>
              <p className="text-xs text-grey mt-0.5">Allow customers to submit credit applications.</p>
            </div>
            <button
              onClick={() => { setFormEnabled(!formEnabled); markChanged() }}
              role="switch"
              aria-checked={formEnabled}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors shrink-0 cursor-pointer ${
                formEnabled ? 'bg-primary' : 'bg-gray-300'
              }`}
            >
              <span className={`inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${
                formEnabled ? 'translate-x-6' : 'translate-x-1'
              }`} />
            </button>
          </div>

          {/* Credit App Type */}
          <div>
            <label htmlFor="credit-app-type" className="block text-sm font-medium text-secondary mb-1.5">Credit App Type</label>
            <select
              id="credit-app-type"
              value={formCreditAppType}
              onChange={(e) => { setFormCreditAppType(e.target.value); markChanged() }}
              disabled={!formEnabled}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none disabled:bg-gray-50 disabled:text-gray-400 bg-white"
            >
              <option value="standard">Standard</option>
              <option value="custom">Custom</option>
            </select>
          </div>

          {/* Notification Email */}
          <div>
            <label htmlFor="notification-email" className="block text-sm font-medium text-secondary mb-1.5">Notification Email</label>
            <input
              id="notification-email"
              type="email"
              value={formNotificationEmail}
              onChange={(e) => { setFormNotificationEmail(e.target.value); markChanged() }}
              placeholder="e.g. finance@dealership.com"
              disabled={!formEnabled}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none disabled:bg-gray-50 disabled:text-gray-400"
            />
            <p className="text-xs text-grey mt-1.5">Email address to receive credit application notifications.</p>
          </div>
        </div>
      </div>
    </div>
  )
}
